import React from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import InputGroup from 'react-bootstrap/InputGroup';
import Alert from 'react-bootstrap/Alert';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';

import '../style/contact.css';
import { sendContactForm } from '../services/backendCalls';

function ContactForm(){
    const { t, i18n } = useTranslation();
    const [selectedCountry, setSelectedCountry] = useState({
        code: 'fr',
        dialCode: '+33',
        name: 'France',
        placeholder: '6 12 34 56 78'
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const searchInputRef = useRef(null);
    const [validated, setValidated] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
        // Clear any previous submission message once the user starts editing again
        if (submitStatus) {
            setSubmitStatus(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            const response = await sendContactForm(formData);
            console.log('Contact form submitted successfully:', response);
            // Reset form and show success confirmation
            setFormData({
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                company: '',
                subject: '',
                message: ''
            });
            setValidated(false);
            setSubmitStatus('success');
        } catch (error) {
            console.error('Error submitting contact form:', error);
            setSubmitStatus('error');
        }
    };
    
    // Comprehensive list of countries with dial codes and placeholders
    const countries = [
        { code: 'fr', dialCode: '+33', name: 'France', placeholder: '6 12 34 56 78' },
        { code: 'us', dialCode: '+1', name: 'United States', placeholder: '(555) 123-4567' },
        { code: 'gb', dialCode: '+44', name: 'United Kingdom', placeholder: '7911 123456' },
        { code: 'de', dialCode: '+49', name: 'Germany', placeholder: '1512 3456789' },
        { code: 'es', dialCode: '+34', name: 'Spain', placeholder: '612 345 678' },
        { code: 'it', dialCode: '+39', name: 'Italy', placeholder: '312 345 6789' },
        { code: 'ca', dialCode: '+1', name: 'Canada', placeholder: '(416) 123-4567' },
        { code: 'au', dialCode: '+61', name: 'Australia', placeholder: '412 345 678' },
        { code: 'br', dialCode: '+55', name: 'Brazil', placeholder: '(11) 91234-5678' },
        { code: 'cn', dialCode: '+86', name: 'China', placeholder: '131 2345 6789' },
        { code: 'in', dialCode: '+91', name: 'India', placeholder: '91234 56789' },
        { code: 'jp', dialCode: '+81', name: 'Japan', placeholder: '090-1234-5678' },
        { code: 'mx', dialCode: '+52', name: 'Mexico', placeholder: '55 1234 5678' },
        { code: 'nl', dialCode: '+31', name: 'Netherlands', placeholder: '6 12345678' },
        { code: 'ru', dialCode: '+7', name: 'Russia', placeholder: '(916) 123-45-67' },
        { code: 'sg', dialCode: '+65', name: 'Singapore', placeholder: '8123 4567' },
        { code: 'za', dialCode: '+27', name: 'South Africa', placeholder: '71 123 4567' },
        { code: 'kr', dialCode: '+82', name: 'South Korea', placeholder: '010-1234-5678' },
        { code: 'se', dialCode: '+46', name: 'Sweden', placeholder: '70 123 45 67' },
        { code: 'ch', dialCode: '+41', name: 'Switzerland', placeholder: '78 123 45 67' },
        { code: 'ae', dialCode: '+971', name: 'United Arab Emirates', placeholder: '50 123 4567' },
        { code: 'be', dialCode: '+32', name: 'Belgium', placeholder: '470 12 34 56' },
        { code: 'dk', dialCode: '+45', name: 'Denmark', placeholder: '20 12 34 56' },
        { code: 'fi', dialCode: '+358', name: 'Finland', placeholder: '40 123 4567' },
        { code: 'gr', dialCode: '+30', name: 'Greece', placeholder: '691 234 5678' },
        { code: 'ie', dialCode: '+353', name: 'Ireland', placeholder: '85 123 4567' },
        { code: 'il', dialCode: '+972', name: 'Israel', placeholder: '50-123-4567' },
        { code: 'nz', dialCode: '+64', name: 'New Zealand', placeholder: '21 123 4567' },
        { code: 'no', dialCode: '+47', name: 'Norway', placeholder: '406 12 345' },
        { code: 'pt', dialCode: '+351', name: 'Portugal', placeholder: '912 345 678' },
        { code: 'sa', dialCode: '+966', name: 'Saudi Arabia', placeholder: '51 234 5678' },
        { code: 'tr', dialCode: '+90', name: 'Turkey', placeholder: '501 234 56 78' },
        { code: 'at', dialCode: '+43', name: 'Austria', placeholder: '664 123456' },
        { code: 'pl', dialCode: '+48', name: 'Poland', placeholder: '512 345 678' },
        { code: 'ro', dialCode: '+40', name: 'Romania', placeholder: '712 345 678' },
        { code: 'ua', dialCode: '+380', name: 'Ukraine', placeholder: '50 123 4567' }
    ];

    // Get translated country name based on current language
    const getTranslatedCountryName = (countryCode) => {
        const currentLanguage = i18n.language;
        if (currentLanguage === 'fr' && t(`contact.phoneInput.countries.${countryCode}`, { defaultValue: '' }) !== '') {
            return t(`contact.phoneInput.countries.${countryCode}`);
        }
        
        // Fallback to English name if translation not available
        const country = countries.find(c => c.code === countryCode);
        return country ? country.name : '';
    };

    // Handle country selection
    const handleCountrySelect = (countryCode) => {
        const country = countries.find(c => c.code === countryCode);
        if (country) {
            setSelectedCountry(country);
            setDropdownOpen(false);
            setSearchTerm('');
        }
    };

    // Filter countries based on search term
    const filteredCountries = countries.filter(country => 
        getTranslatedCountryName(country.code).toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.dialCode.includes(searchTerm)
    );

    // Focus on search input when dropdown opens
    useEffect(() => {
        if (dropdownOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 100);
        }
    }, [dropdownOpen]);

    // Custom dropdown toggle component
    const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
        <Button
            ref={ref}
            variant="outline-secondary"
            onClick={(e) => {
                onClick(e);
                setDropdownOpen(!dropdownOpen);
            }}
            className="country-dropdown-toggle"
        >
            {children}
        </Button>
    ));

    // Custom dropdown menu with search
    const CustomMenu = React.forwardRef(
        ({ style, className, 'aria-labelledby': labeledBy }, ref) => {
            return (
                <div
                    ref={ref}
                    style={style}
                    className={`${className} country-dropdown-menu`}
                    aria-labelledby={labeledBy}
                >
                    <div className="country-search-container px-2 py-2">
                        <Form.Control
                            ref={searchInputRef}
                            autoFocus
                            className="country-search"
                            placeholder={t('contact.phoneInput.searchPlaceholder', 'Search countries...')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="country-list">
                        {filteredCountries.length === 0 ? (
                            <div className="text-center py-2">{t('contact.phoneInput.noCountriesFound', 'No countries found')}</div>
                        ) : (
                            filteredCountries.map(country => (
                                <Dropdown.Item 
                                    key={country.code} 
                                    onClick={() => handleCountrySelect(country.code)}
                                    active={selectedCountry.code === country.code}
                                    className="country-item"
                                >
                                    <span className={`flag-icon flag-icon-${country.code}`}></span>
                                    {getTranslatedCountryName(country.code)} ({country.dialCode})
                                </Dropdown.Item>
                            ))
                        )}
                    </div>
                </div>
            );
        },
    );

    return(
        <section id="contact" className="block contact-block">
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <div className='title-holder'>
                    <h2>{t('contact.title')}</h2>
                    <div className='subtitle'>{t('contact.subtitle')}</div>
                </div>
                {submitStatus === 'success' && (
                    <Alert variant="success" onClose={() => setSubmitStatus(null)} dismissible>
                        {t('contact.successMessage', 'Thank you! Your message has been sent successfully.')}
                    </Alert>
                )}
                {submitStatus === 'error' && (
                    <Alert variant="danger" onClose={() => setSubmitStatus(null)} dismissible>
                        {t('contact.errorMessage', 'Something went wrong while sending your message. Please try again.')}
                    </Alert>
                )}
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="formGroupFirstName">
                            <Form.Label>{t('contact.firstName')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                                required
                                type='text' 
                                name='first_name'
                                placeholder={t('contact.firstName')}
                                value={formData.first_name}
                                onChange={handleInputChange}
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('contact.validation.firstName', 'First name is required.')}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-3" controlId="formGroupLastName">
                            <Form.Label>{t('contact.lastName')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                                required
                                type='text' 
                                name='last_name'
                                placeholder={t('contact.lastName')}
                                value={formData.last_name}
                                onChange={handleInputChange}
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('contact.validation.lastName', 'Last name is required.')}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="formGroupEmail">
                            <Form.Label>{t('contact.email')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                                required
                                type='email' 
                                name='email'
                                placeholder={t('contact.emailPlaceholder')}
                                value={formData.email}
                                onChange={handleInputChange}
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('contact.validation.email', 'Please provide a valid email.')}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-3" controlId="formGroupTel">
                            <Form.Label>{t('contact.phone')}</Form.Label>
                            <InputGroup>
                                <Dropdown 
                                    onToggle={(isOpen) => setDropdownOpen(isOpen)}
                                    show={dropdownOpen}
                                >
                                    <Dropdown.Toggle as={CustomToggle} id="phone-country-dropdown">
                                        <span className={`flag-icon flag-icon-${selectedCountry.code}`}></span>
                                        {selectedCountry.dialCode}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu as={CustomMenu} />
                                </Dropdown>
                                <Form.Control
                                    type="tel"
                                    name="phone"
                                    placeholder={selectedCountry.placeholder}
                                    aria-label="Phone number"
                                    id="formGroupTel"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                />
                            </InputGroup>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="formGroupCompany">
                            <Form.Label>{t('contact.company')}</Form.Label>
                            <Form.Control 
                                type='text' 
                                name='company'
                                placeholder={t('contact.companyPlaceholder')}
                                value={formData.company}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-3" controlId="formGroupSubject">
                            <Form.Label>{t('contact.subject')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                                required
                                type='text' 
                                name='subject'
                                placeholder={t('contact.subjectPlaceholder')}
                                value={formData.subject}
                                onChange={handleInputChange}
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('contact.validation.subject', 'Subject is required.')}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="formGroupMessage">
                            <Form.Label>{t('contact.message')}</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={5} 
                                name='message'
                                placeholder={t('contact.messagePlaceholder')}
                                value={formData.message}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <div className='btn-holder'>
                    <Button type='submit'>{t('contact.send')}</Button>
                </div>
            </Form>
        </section>
    );
}

export default ContactForm;